import { Button, useSnackbar } from '@packages/shared';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Cart32 } from '@packages/themes/icons';
import type { SxProps } from '@mui/system';
import { defineMessages, useIntl } from 'react-intl';
import { AddToBasketButton } from '@packages/modules/src/AddToBasketButton/index';
import { MiniDVLayer } from '@packages/modules/src/MiniDVLayer/MiniDVLayer';
import { useState } from 'react';
import { WishlistFragmentFragmentDoc } from '@packages/gql/generated/shopping/WishlistFragmentFragmentDoc';
import {
  type FragmentType,
  getFragmentData,
  makeFragmentData,
} from '@packages/gql/generated/shopping';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { BasketActionsFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketActionsFragmentFragmentDoc';
import { AddToBasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/AddToBasketFragmentFragmentDoc';
import type { GTMEventGlycerinAddToCart } from '@packages/tracking/src/types/events';
import type {
  GlycerinCartProduct,
  GlycerinWishListProduct,
} from '@packages/tracking/src/types/types';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { buttonStyling, iconLargerStyling, iconStyling } from './Actions.styles';
import { GraphqlDocuments } from './mappedModeToDocument';

export interface BasketActionProps {
  data: FragmentType<typeof BasketActionsFragmentFragmentDoc>;
  glycerinProduct: GlycerinCartProduct | GlycerinWishListProduct;
  overrideSx?: SxProps;
  galleryView?: boolean;
}

const messages = defineMessages({
  buttonTextShort: {
    id: 'BasketAction.ButtonText',
    defaultMessage: 'Warenkorb',
    description: 'this will be displayed on the button',
  },
  buttonTextLong: {
    id: 'BasketAction.ButtonText.Long',
    defaultMessage: 'in den Warenkorb',
    description: 'this will be displayed on the button',
  },
  successMessage: {
    id: 'BasketAction.SuccessMessage',
    defaultMessage: 'Der Artikel wurde erfolgreich in Ihren Warenkorb gelegt',
  },
});
/* GraphQL */ `
  fragment BasketActionsFragment on WishlistLineItem {
    sku
    akl
    isSoldOut
    price {
      currency
      saving {
        value
      }
      value
      valueOld
    }
    unspecified
  }
`;
export const BasketAction: React.FC<BasketActionProps> = ({
  data,
  overrideSx,
  galleryView,
  glycerinProduct,
}) => {
  const [open, setOpen] = useState(false);
  const { locale } = useI18n();
  const { formatMessage } = useIntl();
  const { addToast } = useSnackbar();
  const { isMobile } = useDeviceType();
  const dispatchGlycerin = useTracking();
  const { akl, isSoldOut, price, sku, unspecified } = getFragmentData(
    BasketActionsFragmentFragmentDoc,
    data,
  );

  const [wishListState, removeFromWishlist] = useAuthMutation(GraphqlDocuments.wishlist);
  const handleRemoveFromWishlist = () => {
    setOpen(false);
    // grab original lineItemSku make call against backend to remove from wishlist
    // this is a callback function that will only occur when the user clicks to add to basket
    if (wishListState.fetching) return;
    removeFromWishlist({ locale, sku })
      .then((result) => {
        if (result.error) throw new Error('error');
        const removeFromWishlistResult = result.data?.removeFromWishlist;
        const wishlist = getFragmentData(WishlistFragmentFragmentDoc, removeFromWishlistResult);
        if (wishlist) {
          addToast({
            severity: 'success',
            message: formatMessage(messages.successMessage),
            duration: 5000,
            onClose: () => {},
          });
        }
      })
      .catch(() => {
        addToast({
          severity: 'error',
          message: 'Fehler beim Entfernen des Artikels',
          duration: 5000,
          onClose: () => {},
        });
      });
  };
  const handleOpenDialog = () => {
    setOpen(true);
    // this will fire without having to wait for the response from the backend, if you want to wait for the response use handleRemoveFromWishlist instead
    //   if (!state.fetching && lineItemId && isGlycerinCartProduct(glycerinProduct)) {
    //         dispatchGlycerin<GTMEventGlycerinRemoveFromCart>({
    //           event: 'RemoveFromCart',
    //           RemoveFromCartData: {
    //             removedProducts: [glycerinProduct],
    //           },
    //         });
    //         // display cart is fired from basket component due to its rerendering
    //         dispatch('basket_article', 'delete');
    //       }
    //     });
    //   }
    dispatchGlycerin<GTMEventGlycerinAddToCart>({
      event: 'AddToCart',
      AddToCartData: {
        addedProducts: [
          {
            ...glycerinProduct,
            quantity: 1,
            articleId: glycerinProduct.articleId || '',
            skuId: glycerinProduct.skuId || '',
          },
        ],
      },
    });
  };

  if (isSoldOut) return null;
  const mobileAvailableListView = isMobile && !galleryView;
  const desktopAvailableListView = !isMobile && !galleryView;
  const styles = {
    ...buttonStyling,
    ...overrideSx,
  };
  const currIconStyling: SxProps = desktopAvailableListView ? iconStyling : iconLargerStyling;
  const customButtonText = (() => {
    if (galleryView) {
      return null;
    }
    if (mobileAvailableListView) {
      return messages.buttonTextLong;
    }
    return messages.buttonTextShort;
  })();

  return !unspecified && price !== null ? (
    <AddToBasketButton
      data={makeFragmentData(
        {
          akl,
          sku,
          availability: {
            isOrderable: !isSoldOut,
            state: !unspecified ? 'AVAILABLE' : 'UNSELECTED',
          },
          price: {
            currency: price.currency === 'EUR' || price.currency === 'CHF' ? price.currency : 'EUR',
            saving: price?.saving?.value !== undefined ? { value: price.saving.value } : null,
            value: price?.value,
            valueOld: price?.valueOld,
          },
          marketcode: null,
        },
        AddToBasketFragmentFragmentDoc,
      )}
      color="inherit"
      data-testid="basket-action"
      size={desktopAvailableListView ? 'medium' : 'large'}
      onClose={handleRemoveFromWishlist}
      sx={{ ...styles, ...{ flexDirection: 'row-reverse' } }}
      customButtonText={customButtonText}
      hideEndIcon={mobileAvailableListView}
      iconSx={{ ...currIconStyling, ...(!galleryView ? { ml: -1, mr: 1 } : {}) }}
    />
  ) : (
    <>
      <Button
        sx={styles}
        onClick={handleOpenDialog}
        data-testid="basket-action"
        size={desktopAvailableListView ? 'medium' : 'large'}
        startIcon={mobileAvailableListView ? null : <Cart32 sx={currIconStyling} />}
      >
        {customButtonText !== null && formatMessage(customButtonText)}
      </Button>
      <MiniDVLayer
        sku={sku}
        akl={akl}
        open={open}
        quantity={1}
        onClick={handleRemoveFromWishlist}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
